/**
 * Production Logger - replaces console.log statements with structured logging
 * Removes all console.log from production builds for security and performance
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  context?: Record<string, any>;
  userId?: string;
  hotelId?: string;
  component?: string;
  stack?: string;
}

export class ProductionLogger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private maxLogEntries = 1000;
  private logBuffer: LogEntry[] = [];
  private logHandlers: Array<(entry: LogEntry) => void> = [];

  constructor() {
    // Only keep logs in memory for development
    if (this.isDevelopment) {
      this.setupDevelopmentHandlers();
    } else {
      this.setupProductionHandlers();
    }
  }

  private setupDevelopmentHandlers() {
    // In development, allow console output with colors
    this.addHandler((entry) => {
      const colors = {
        debug: '\x1b[36m',    // Cyan
        info: '\x1b[32m',     // Green
        warn: '\x1b[33m',     // Yellow
        error: '\x1b[31m',    // Red
        critical: '\x1b[35m'  // Magenta
      };
      
      const reset = '\x1b[0m';
      const color = colors[entry.level];
      const timestamp = new Date(entry.timestamp).toISOString();
      
      const prefix = `${color}[${entry.level.toUpperCase()}]${reset} ${timestamp}`;
      const component = entry.component ? `[${entry.component}]` : '';
      const context = entry.context ? JSON.stringify(entry.context) : '';
      
      console.log(`${prefix} ${component} ${entry.message} ${context}`);
      
      if (entry.stack && entry.level === 'error') {
        console.error(entry.stack);
      }
    });
  }

  private setupProductionHandlers() {
    // In production, only log critical errors and send to monitoring
    this.addHandler((entry) => {
      if (entry.level === 'critical' || entry.level === 'error') {
        // Send to monitoring service (could be Sentry, LogRocket, etc.)
        this.sendToMonitoring(entry);
      }
      
      // Store in buffer for debugging
      this.addToBuffer(entry);
    });
  }

  private addHandler(handler: (entry: LogEntry) => void) {
    this.logHandlers.push(handler);
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxLogEntries) {
      this.logBuffer.shift(); // Remove oldest entry
    }
  }

  private sendToMonitoring(entry: LogEntry) {
    // In a real implementation, send to your monitoring service
    // For now, we'll use the structured logging to audit table
    if (typeof window !== 'undefined' && 'supabase' in window) {
      try {
        (window as any).supabase?.rpc('log_admin_action', {
          p_action_type: 'application_error',
          p_target_table: 'system_logs',
          p_new_data: {
            level: entry.level,
            message: entry.message,
            context: entry.context,
            component: entry.component,
            timestamp: entry.timestamp,
            user_id: entry.userId,
            hotel_id: entry.hotelId
          }
        }).catch(() => {
          // Fail silently in production
        });
      } catch {
        // Fail silently
      }
    }
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    component?: string
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      context,
      component
    };

    // Add stack trace for errors
    if (level === 'error' || level === 'critical') {
      entry.stack = new Error().stack;
    }

    return entry;
  }

  debug(message: string, context?: Record<string, any>, component?: string) {
    if (!this.isDevelopment) return; // Don't log debug in production
    
    const entry = this.createLogEntry('debug', message, context, component);
    this.logHandlers.forEach(handler => handler(entry));
  }

  info(message: string, context?: Record<string, any>, component?: string) {
    const entry = this.createLogEntry('info', message, context, component);
    this.logHandlers.forEach(handler => handler(entry));
  }

  warn(message: string, context?: Record<string, any>, component?: string) {
    const entry = this.createLogEntry('warn', message, context, component);
    this.logHandlers.forEach(handler => handler(entry));
  }

  error(message: string, context?: Record<string, any>, component?: string) {
    const entry = this.createLogEntry('error', message, context, component);
    this.logHandlers.forEach(handler => handler(entry));
  }

  critical(message: string, context?: Record<string, any>, component?: string) {
    const entry = this.createLogEntry('critical', message, context, component);
    this.logHandlers.forEach(handler => handler(entry));
  }

  // Performance logging
  time(label: string, component?: string) {
    if (!this.isDevelopment) return;
    console.time(`[${component || 'PERF'}] ${label}`);
  }

  timeEnd(label: string, component?: string) {
    if (!this.isDevelopment) return;
    console.timeEnd(`[${component || 'PERF'}] ${label}`);
  }

  // Get logs for debugging
  getLogs(level?: LogLevel): LogEntry[] {
    if (!level) return this.logBuffer;
    return this.logBuffer.filter(entry => entry.level === level);
  }

  // Clear logs
  clearLogs() {
    this.logBuffer = [];
  }

  // Get log statistics
  getStats() {
    const stats = {
      total: this.logBuffer.length,
      byLevel: {} as Record<LogLevel, number>
    };

    this.logBuffer.forEach(entry => {
      stats.byLevel[entry.level] = (stats.byLevel[entry.level] || 0) + 1;
    });

    return stats;
  }
}

// Create singleton instance
export const logger = new ProductionLogger();

// Utility functions for component-specific logging
export const createComponentLogger = (componentName: string) => ({
  debug: (message: string, context?: Record<string, any>) => 
    logger.debug(message, context, componentName),
  info: (message: string, context?: Record<string, any>) => 
    logger.info(message, context, componentName),
  warn: (message: string, context?: Record<string, any>) => 
    logger.warn(message, context, componentName),
  error: (message: string, context?: Record<string, any>) => 
    logger.error(message, context, componentName),
  critical: (message: string, context?: Record<string, any>) => 
    logger.critical(message, context, componentName),
  time: (label: string) => logger.time(label, componentName),
  timeEnd: (label: string) => logger.timeEnd(label, componentName)
});

// Replace console.log in production
if (process.env.NODE_ENV === 'production') {
  // Override console methods to prevent accidental logging
  const noop = () => {};
  
  // Keep error and warn for critical issues
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.trace = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
}
